import React from "react";
import A4Sheet from "./A4Sheet";
import { FONT_MAP } from "../lib/format";


const isLightColor = (color) => {
  if (!color) return true;
  let h = String(color).replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length < 6) return true;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
};

// Mix a hex color with black (amount<0) or white (amount>0). amount in [-1, 1].
const shade = (hex, amount) => {
  let h = String(hex || "#4A3BFA").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length < 6) h = "4A3BFA";
  const to = amount < 0 ? 0 : 255, t = Math.abs(amount);
  const ch = (i) => Math.round(parseInt(h.slice(i, i + 2), 16) * (1 - t) + to * t).toString(16).padStart(2, "0");
  return `#${ch(0)}${ch(2)}${ch(4)}`;
};

// Default cover background derived from the brand color: darker for dark layouts, lighter tint for light ones.
export const coverBgFor = (layout, primary) => {
  const kind = coverKind(layout);
  if (kind === "gradient") return null;
  if (kind === "light") return shade(primary, 0.9);
  if (kind === "mono") return shade(primary, 0.93);
  return shade(primary, -0.6);
};

const coverKind = (layout) => {
  if (["glass_aurora", "gradient_wave"].includes(layout)) return "gradient";
  if (["minimal_classic", "creative_studio", "soft_pastel"].includes(layout)) return "light";
  if (["compact_tech", "mono_grid"].includes(layout)) return "mono";
  return "dark"; // corporate_bold, prestige_dark, modern_editorial
};

const BasiqBrand = ({ color = "rgba(148,163,184,0.9)" }) => (
  <div style={{ position: "absolute", left: 0, right: 0, bottom: 18, textAlign: "center", fontSize: 9, letterSpacing: "0.08em", color, pointerEvents: "none" }} data-testid="basiq-brand">
    Feito com <span style={{ fontWeight: 700 }}>Basiq</span>
  </div>
);

const CoverPage = ({ company = {}, theme = {}, data = {}, layout, branding = false }) => {
  const primary = theme.primary || "#4A3BFA";
  const fontClass = FONT_MAP[theme.font] || "tpl-sans";
  const coverTitle = theme.coverTitle || "Proposta Comercial";
  const coverImage = theme.coverImage;
  const kind = coverKind(layout);

  const customBg = (theme.coverBg || "").trim();
  let bg = coverBgFor(layout, primary) || "#0f172a";
  if (kind === "gradient") bg = `linear-gradient(130deg, ${shade(primary, -0.15)}, ${primary} 55%, ${shade(primary, 0.35)})`;
  if (customBg) bg = customBg;

  const solidBg = customBg || (kind === "gradient" ? primary : bg);
  const useDark = isLightColor(solidBg);
  const fg = useDark ? "#0f172a" : "#fff";
  const sub = useDark ? "#64748b" : "rgba(255,255,255,0.6)";
  const subStrong = useDark ? "#0f172a" : "rgba(255,255,255,0.9)";
  const logoFilter = useDark ? "none" : "brightness(0) invert(1)";
  const showBorder = useDark;
  const barBg = useDark ? primary : "rgba(255,255,255,0.85)";
  const mono = kind === "mono" ? "font-mono-jb" : "";
  const serifTitle = kind === "light" && theme.font === "serif";

  return (
    <div className={`a4-sheet ${fontClass}`} style={{ position: "relative", background: bg, color: fg, padding: 56, display: "flex", flexDirection: "column", justifyContent: "space-between", border: showBorder ? "1px solid #eef2f6" : "none" }} data-testid="a4-cover">
      <div className="flex items-center justify-between">
        {company.logo ? (
          <img src={company.logo} alt="logo" style={{ maxHeight: 48, maxWidth: 180, objectFit: "contain", filter: logoFilter }} />
        ) : (
          <div className={`text-[15px] font-semibold tracking-tight ${mono}`}>{company.name || ""}</div>
        )}
        {data.issue_date && <div className={`text-[11px] font-mono-jb`} style={{ color: sub }}>{data.issue_date}</div>}
      </div>

      <div>
        <div className="rounded-full mb-8" style={{ height: kind === "mono" ? 2 : 4, width: 64, background: barBg }} />
        <h1 className={`${serifTitle ? "tpl-serif-head text-6xl" : "text-5xl font-bold"} tracking-tight leading-[1.03] ${mono}`} style={{ maxWidth: 540 }}>{coverTitle}</h1>
        {coverImage && <img src={coverImage} alt="" crossOrigin="anonymous" className="mt-8 w-full rounded-2xl object-cover" style={{ maxHeight: 260 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
        <div className="mt-12 space-y-4" style={{ maxWidth: 460 }}>
          {data.client_name && (
            <div>
              <div className={`text-[10px] uppercase tracking-[0.2em] ${mono}`} style={{ color: sub }}>Preparado para</div>
              <div className="text-[18px] font-medium mt-1" style={{ color: subStrong }}>{data.client_name}</div>
            </div>
          )}
          {data.title && (
            <div>
              <div className={`text-[10px] uppercase tracking-[0.2em] ${mono}`} style={{ color: sub }}>Assunto</div>
              <div className="text-[15px] mt-1" style={{ color: subStrong }}>{data.title}</div>
            </div>
          )}
        </div>
      </div>

      <div className={`text-[11px] ${mono}`} style={{ color: sub }}>{[company.name, company.email, company.phone].filter(Boolean).join("  ·  ")}</div>
      {branding && <BasiqBrand color={sub} />}
    </div>
  );
};

const BackCoverPage = ({ company = {}, theme = {}, layout, branding = false }) => {
  const primary = theme.primary || "#4A3BFA";
  const fontClass = FONT_MAP[theme.font] || "tpl-sans";
  const customBg = (theme.coverBg || "").trim();
  const bg = customBg || coverBgFor(layout, primary) || shade(primary, -0.6);
  const useDark = isLightColor(bg);
  const fg = useDark ? "#0f172a" : "#fff";
  const sub = useDark ? "#64748b" : "rgba(255,255,255,0.7)";
  const contact = [company.email, company.phone, company.web, company.address].filter(Boolean);
  return (
    <div className={`a4-sheet ${fontClass}`} style={{ position: "relative", background: bg, color: fg, padding: 56, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", border: useDark ? "1px solid #eef2f6" : "none" }} data-testid="a4-backcover">
      <div className="rounded-full mb-8" style={{ height: 4, width: 64, background: useDark ? primary : "rgba(255,255,255,0.85)" }} />
      <div className="text-3xl font-semibold tracking-tight">Obrigado!</div>
      <p className="text-[13px] mt-4 max-w-sm" style={{ color: sub }}>Ficamos à disposição para dúvidas e para dar início ao projeto assim que a proposta for aprovada.</p>
      {company.name && <div className="mt-10 text-[15px] font-medium">{company.name}</div>}
      {contact.length > 0 && <div className="mt-2 text-[11px]" style={{ color: sub }}>{contact.join("  ·  ")}</div>}
      {branding && <BasiqBrand color={sub} />}
    </div>
  );
};

export default function A4Document({ layout, company, theme = {}, data, onChange, editable = false, branding = false }) {
  return (
    <div className="a4-doc space-y-6">
      {theme.cover && <CoverPage company={company} theme={theme} data={data} layout={layout} branding={branding} />}
      <A4Sheet layout={layout} company={company} theme={theme} data={data} onChange={onChange} editable={editable} branding={branding} />
      {theme.cover && <BackCoverPage company={company} theme={theme} layout={layout} branding={branding} />}
    </div>
  );
}
